import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { 
  Building2, 
  Users, 
  Settings, 
  Save, 
  RefreshCw,
  RefreshCcw,
  Shield,
  Key,
  Webhook,
  Trash2,
  Eye,
  EyeOff,
  UserCog,
  AlertTriangle,
  AlertCircle,
  Copy,
  Check,
  Wifi,
  Camera,
  Upload,
  User,
  Phone,
  Cloud,
  Server,
  HelpCircle,
  ExternalLink,
  Info,
  Plus,
  Zap,
  ToggleLeft,
  ToggleRight,
  ArrowRight,
  Hash,
  MoreVertical,
  UserX,
  UserCheck,
  X,
  Voicemail
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from 'sonner';
import { StarCodesManager } from '@/components/StarCodesManager';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { API_URL } from '../utils/api';
const API = API_URL;

export default function CompanySettings() {
  const { token, user, isDeveloper, isCustomerAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    return ['company', 'pbx', 'starcodes', 'users', 'lines'].includes(tabParam) ? tabParam : 'company';
  });
  
  // Company settings state
  const [company, setCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    api_base_url: '',
    api_username: '',
    api_password: '',
    contacts_url: '',
    voicemail_url: ''
  });
  const [showApiPassword, setShowApiPassword] = useState(false);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Image upload state
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  // Webhook state (read-only display)
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [copied, setCopied] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  
  // Webhook relay state
  const [relays, setRelays] = useState([]);
  const [testingRelay, setTestingRelay] = useState(null);

  // Lines state (for All Lines tab)
  const [lines, setLines] = useState([]);
  const [loadingLines, setLoadingLines] = useState(false);
  const [linesFilter, setLinesFilter] = useState('all');

  // Fetch webhook URL and secret
  const fetchWebhookConfig = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [urlRes, secretRes, relayRes] = await Promise.all([
        axios.get(`${API}/webhooks/url`, { headers }),
        axios.get(`${API}/webhooks/secret`, { headers }),
        axios.get(`${API}/webhooks/relay`, { headers })
      ]);
      setWebhookUrl(urlRes.data.webhook_url);
      setWebhookSecret(secretRes.data.secret);
      setRelays(relayRes.data.relays || []);
    } catch (error) {
      console.error("Failed to fetch webhook config:", error);
    }
  };

  const regenerateSecret = async () => {
    try {
      const res = await axios.post(`${API}/webhooks/secret/regenerate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setWebhookSecret(res.data.secret);
      toast.success("Secret token regenerated");
    } catch (error) {
      toast.error("Failed to regenerate secret");
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    toast.success("Webhook URL copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const copySecret = () => {
    navigator.clipboard.writeText(webhookSecret);
    setCopiedSecret(true);
    toast.success("Secret token copied!");
    setTimeout(() => setCopiedSecret(false), 2000);
  };

  // Relay management functions
  const deleteRelay = async (relayId) => {
    try {
      await axios.delete(`${API}/webhooks/relay/${relayId}`, { headers: { Authorization: `Bearer ${token}` } });
      setRelays(relays.filter(r => r.id !== relayId));
      toast.success("Relay removed");
    } catch (error) {
      toast.error("Failed to remove relay");
    }
  };

  const toggleRelay = async (relayId) => {
    try {
      const res = await axios.put(`${API}/webhooks/relay/${relayId}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setRelays(relays.map(r => r.id === relayId ? {...r, enabled: res.data.enabled} : r));
      toast.success(res.data.enabled ? "Relay enabled" : "Relay disabled");
    } catch (error) {
      toast.error("Failed to toggle relay");
    }
  };

  const testRelay = async (url) => {
    setTestingRelay(url);
    try {
      const res = await axios.post(`${API}/webhooks/relay/test`, { url }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.status === "ok") {
        toast.success(`Test successful! Status: ${res.data.response_status}`);
      } else {
        toast.error(`Test failed: ${res.data.error}`);
      }
    } catch (error) {
      toast.error("Test failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setTestingRelay(null);
    }
  };

  // Fetch company settings
  const fetchCompanySettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/company/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.is_developer && res.data.companies) {
        // Developer viewing - show first company or empty state
        if (res.data.companies.length > 0) {
          setCompany(res.data.companies[0]);
          setCompanyForm({
            name: res.data.companies[0].name || '',
            api_base_url: res.data.companies[0].api_base_url || '',
            api_username: res.data.companies[0].api_credentials?.username || '',
            api_password: '',
            contacts_url: res.data.companies[0].contacts_url || '',
            voicemail_url: res.data.companies[0].voicemail_url || ''
          });
        }
      } else {
        setCompany(res.data);
        setCompanyForm({
          name: res.data.name || '',
          api_base_url: res.data.api_base_url || '',
          api_username: res.data.api_credentials?.username || '',
          api_password: '',
          contacts_url: res.data.contacts_url || '',
          voicemail_url: res.data.voicemail_url || ''
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  // Fetch company users
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API}/company/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchLines = async () => {
    setLoadingLines(true);
    try {
      const res = await axios.get(`${API}/connectus/lines`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLines(res.data?.Data || []);
    } catch (error) {
      console.error('Failed to load lines:', error);
    } finally {
      setLoadingLines(false);
    }
  };

  const toggleLineQueue = async (serviceName, isQueue) => {
    try {
      await axios.post(`${API}/connectus/lines/set-queue`, 
        { service_name: serviceName, is_queue: isQueue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isQueue ? `Marked ${serviceName} as queue` : `Removed queue from ${serviceName}`);
      fetchLines();
    } catch (error) {
      toast.error('Failed to update queue status');
    }
  };

  const toggleLineAA = async (serviceName, hasAA) => {
    try {
      await axios.post(`${API}/connectus/lines/set-auto-attendant`, 
        { service_name: serviceName, has_auto_attendant: hasAA },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(hasAA ? `Enabled AA for ${serviceName}` : `Disabled AA for ${serviceName}`);
      fetchLines();
    } catch (error) {
      toast.error('Failed to update auto-attendant status');
    }
  };

  const toggleLineAH = async (serviceName, isAH) => {
    try {
      await axios.post(`${API}/connectus/lines/set-after-hours`, 
        { service_name: serviceName, is_after_hours: isAH },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isAH ? `Marked ${serviceName} as after-hours` : `Removed after-hours from ${serviceName}`);
      fetchLines();
    } catch (error) {
      toast.error('Failed to update after-hours status');
    }
  };

  const toggleLineMain = async (serviceName, isMain) => {
    try {
      await axios.post(`${API}/connectus/lines/set-main-line`, 
        { service_name: serviceName, is_main_line: isMain },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isMain ? `Marked ${serviceName} as main line` : `Removed main line from ${serviceName}`);
      fetchLines();
    } catch (error) {
      toast.error('Failed to update main line status');
    }
  };

  useEffect(() => {
    fetchCompanySettings();
    fetchUsers();
    fetchWebhookConfig();
  }, [token]);

  // Fetch lines when Lines tab is selected
  useEffect(() => {
    if (activeTab === 'lines' && lines.length === 0) {
      fetchLines();
    }
  }, [activeTab]);

  // Save company settings
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const updateData = {
        name: companyForm.name || null,
        api_base_url: companyForm.api_base_url || null,
        api_username: companyForm.api_username || null,
        contacts_url: companyForm.contacts_url || null,
        voicemail_url: companyForm.voicemail_url || null
      };
      
      // Only include password if provided
      if (companyForm.api_password) {
        updateData.api_password = companyForm.api_password;
      }

      await axios.put(`${API}/company/settings`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Company settings saved');
      setCompanyForm(prev => ({ ...prev, api_password: '' }));
      fetchCompanySettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Update user role
  const handleRoleChange = async (userId, newRole) => {
    // Check if this would leave no customer admins
    if (!canChangeRole(userId, newRole)) {
      toast.error('Cannot demote: There must be at least one Customer Admin in the company');
      return;
    }
    
    try {
      await axios.put(
        `${API}/company/users/${userId}/role`,
        null,
        { 
          params: { role: newRole },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('User role updated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  // Remove user
  const handleRemoveUser = async (userId) => {
    try {
      await axios.delete(`${API}/company/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User permanently deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleUserStatus = async (userId, isActive) => {
    try {
      await axios.put(`${API}/company/users/${userId}/status`, 
        { is_active: isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isActive ? 'User enabled' : 'User disabled');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  // Get role badge
  const getRoleBadge = (role) => {
    const roleMap = {
      developer: { label: 'Developer', variant: 'destructive' },
      customer_admin: { label: 'Admin', variant: 'default' },
      phone_admin: { label: 'Phone Admin', variant: 'secondary' },
      standard: { label: 'User', variant: 'outline' }
    };
    const r = roleMap[role] || roleMap.standard;
    return <Badge variant={r.variant}>{r.label}</Badge>;
  };

  // Available roles for selection - Customer Admins can assign customer_admin role
  const getAvailableRoles = () => {
    const roles = [
      { value: 'standard', label: 'Standard User' },
      { value: 'phone_admin', label: 'Phone Admin' }
    ];
    // Customer admins can promote others to customer_admin
    if (isDeveloper() || isCustomerAdmin()) {
      roles.push({ value: 'customer_admin', label: 'Customer Admin' });
    }
    if (isDeveloper()) {
      roles.push({ value: 'developer', label: 'Developer' });
    }
    return roles;
  };

  // Check if demoting a customer_admin would leave no admins
  const canChangeRole = (userId, newRole) => {
    if (newRole === 'customer_admin') return true;
    
    const targetUser = users.find(u => u.id === userId);
    if (!targetUser || targetUser.role !== 'customer_admin') return true;
    
    // Count remaining customer_admins if this one is demoted
    const adminCount = users.filter(u => 
      u.role === 'customer_admin' && u.id !== userId
    ).length;
    
    return adminCount >= 1;
  };

  // Handle image upload for a user
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedUser) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          await axios.put(
            `${API}/users/${selectedUser.id}/profile-image`,
            { image_data: reader.result },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success(`Profile image updated for ${selectedUser.name || selectedUser.phone_number}`);
          setImageDialogOpen(false);
          setSelectedUser(null);
          fetchUsers();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to upload image');
        } finally {
          setUploadingImage(false);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read image file');
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to process image');
      setUploadingImage(false);
    }

    // Reset file input
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  // Remove user profile image
  const handleRemoveImage = async () => {
    if (!selectedUser) return;
    
    setUploadingImage(true);
    try {
      await axios.delete(
        `${API}/users/${selectedUser.id}/profile-image`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Profile image removed for ${selectedUser.name || selectedUser.phone_number}`);
      setImageDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Open image dialog for a user
  const openImageDialog = (u) => {
    setSelectedUser(u);
    setImageDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="company-settings-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
          <Building2 className="w-8 h-8 text-primary" />
          Company Settings
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Manage your company&apos;s configuration and team members
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-3xl grid-cols-5">
          <TabsTrigger value="company" className="flex items-center gap-2" data-testid="tab-company">
            <Building2 className="w-4 h-4" />
            Company
          </TabsTrigger>
          <TabsTrigger value="pbx" className="flex items-center gap-2" data-testid="tab-pbx">
            <Phone className="w-4 h-4" />
            PBX
          </TabsTrigger>
          <TabsTrigger value="lines" className="flex items-center gap-2" data-testid="tab-lines">
            <Server className="w-4 h-4" />
            Lines
          </TabsTrigger>
          <TabsTrigger value="starcodes" className="flex items-center gap-2" data-testid="tab-starcodes">
            <Hash className="w-4 h-4" />
            Star Codes
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2" data-testid="tab-users">
            <Users className="w-4 h-4" />
            Team
          </TabsTrigger>
        </TabsList>

        {/* Company Settings Tab */}
        <TabsContent value="company" className="space-y-6">
          {/* Company Info */}
          <Card className="border-border/50" data-testid="company-info-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Company Information
              </CardTitle>
              <CardDescription>Basic company details and identification</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={companyForm.name}
                    onChange={e => setCompanyForm({ ...companyForm, name: e.target.value })}
                    placeholder="Your company name"
                    data-testid="company-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Account Number</Label>
                  <Input
                    value={company?.account_number || ''}
                    disabled
                    className="bg-secondary/30"
                  />
                  <p className="text-xs text-muted-foreground">Share this with team members to let them register</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-company-btn">
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Company Info
            </Button>
          </div>
        </TabsContent>

        {/* PBX Integration Tab */}
        <TabsContent value="pbx" className="space-y-6">
          {/* Provider Info Banner */}
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Cloud className="w-6 h-6 text-primary mt-0.5" />
                <div>
                  <h3 className="font-semibold text-lg">Hero Cloud PBX</h3>
                  <p className="text-sm text-muted-foreground">
                    Your company is connected to Hero Cloud PBX (Connect-US). Configure your API credentials and webhooks below to enable call features.
                  </p>
                </div>
                <Badge variant="outline" className="border-emerald-500 text-emerald-500 ml-auto shrink-0">
                  Active
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* API Base URL Section */}
          <Card className="border-border/50" data-testid="api-base-url-card">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="w-5 h-5 text-primary" />
                    API Endpoint
                  </CardTitle>
                  <CardDescription>
                    Your Connect-US portal URL for API access
                  </CardDescription>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <HelpCircle className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs">
                      <p>This is your company&apos;s specific Connect-US portal URL. Different companies may have different portals (e.g., admin1, admin2, etc.)</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>API Base URL</Label>
                <Input
                  value={companyForm.api_base_url}
                  onChange={e => setCompanyForm({ ...companyForm, api_base_url: e.target.value })}
                  placeholder="https://admin1.connect-us.co.nz"
                  data-testid="api-base-url-input"
                />
                <p className="text-xs text-muted-foreground">
                  Common URLs: <code className="bg-secondary/50 px-1 rounded">admin1.connect-us.co.nz</code>, <code className="bg-secondary/50 px-1 rounded">admin2.connect-us.co.nz</code>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* API Credentials Section */}
          <Card className="border-border/50" data-testid="api-credentials-card">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5 text-primary" />
                    API Credentials
                  </CardTitle>
                  <CardDescription>
                    Required for Click-to-Call, CDR sync, and contact management
                  </CardDescription>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <HelpCircle className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs">
                      <p>Find these credentials in your Connect-US portal under API Settings. Contact Hero support if you need help.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>API Username</Label>
                  <Input
                    value={companyForm.api_username}
                    onChange={e => setCompanyForm({ ...companyForm, api_username: e.target.value })}
                    placeholder="Your Connect-US API username"
                    data-testid="api-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Password</Label>
                  <div className="relative">
                    <Input
                      type={showApiPassword ? 'text' : 'password'}
                      value={companyForm.api_password}
                      onChange={e => setCompanyForm({ ...companyForm, api_password: e.target.value })}
                      placeholder={company?.api_credentials?.has_password ? '••••••••' : 'API password'}
                      className="pr-10"
                      data-testid="api-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiPassword(!showApiPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showApiPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {company?.api_credentials?.has_password && (
                    <p className="text-xs text-emerald-500">✓ Password configured</p>
                  )}
                </div>
              </div>
              <div className="bg-secondary/30 rounded-lg p-3 text-sm">
                <p className="font-medium text-foreground/80 mb-1">What are these used for?</p>
                <ul className="text-muted-foreground space-y-1 text-xs">
                  <li>• <strong>Click-to-Call:</strong> Initiate calls from the app to your desk phone</li>
                  <li>• <strong>CDR Sync:</strong> Fetch call history and analytics</li>
                  <li>• <strong>Contact Management:</strong> Push contacts to your Hero phonebook</li>
                </ul>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Contacts Sync URL</Label>
                <Input
                  value={companyForm.contacts_url}
                  onChange={e => setCompanyForm({ ...companyForm, contacts_url: e.target.value })}
                  placeholder="https://portal1.connect-us.co.nz/api/contacts.php?l=..."
                  data-testid="company-contacts-url"
                />
                <p className="text-xs text-muted-foreground">External URL to sync phonebook contacts from your PBX portal</p>
              </div>
            </CardContent>
          </Card>

          {/* Voicemail Server Section */}
          <Card className="border-border/50" data-testid="voicemail-config-card">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Voicemail className="w-5 h-5 text-primary" />
                    Voicemail Server
                  </CardTitle>
                  <CardDescription>
                    Configure the voicemail server URL for accessing user voicemails
                  </CardDescription>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <HelpCircle className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs">
                      <p>This is the URL of the Horde webmail server where voicemails are stored. Contact your PBX provider for this URL.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Voicemail Server URL</Label>
                <Input
                  value={companyForm.voicemail_url}
                  onChange={e => setCompanyForm({ ...companyForm, voicemail_url: e.target.value })}
                  placeholder="https://line1.connect-us.co.nz"
                  data-testid="voicemail-url-input"
                />
                <p className="text-xs text-muted-foreground">
                  The Horde webmail server URL where voicemails are stored. Users authenticate with their SIP credentials.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Webhook Configuration Section */}
          <Card className="border-border/50" data-testid="webhook-config-card">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Wifi className="w-5 h-5 text-primary" />
                    Webhook Configuration
                  </CardTitle>
                  <CardDescription>
                    Receive real-time call events (ringing, answered, ended)
                  </CardDescription>
                </div>
                <a 
                  href="https://portal1.connect-us.co.nz/apidocs/webhooks/index.php" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline text-sm flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  Setup Guide
                </a>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Instructions */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-600 dark:text-blue-400">Setup Instructions</p>
                    <ol className="text-muted-foreground text-xs mt-1 space-y-1 list-decimal list-inside">
                      <li>Log into your Connect-US portal</li>
                      <li>Navigate to API Settings → Webhooks</li>
                      <li>Create a new webhook using the URL and Secret below</li>
                      <li>Select events: Call Ringing, Call Answered, Call Ended</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Webhook URL */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Webhook URL</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-secondary/50 rounded text-sm font-mono truncate border border-border/50">
                    {webhookUrl || "Loading..."}
                  </code>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={copyWebhookUrl}
                    disabled={!webhookUrl}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Secret Token */}
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  Secret Token
                  <Badge variant="outline" className="text-xs">For verification</Badge>
                </Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-secondary/50 rounded text-sm font-mono border border-border/50">
                    {webhookSecret || "Loading..."}
                  </code>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={copySecret}
                    disabled={!webhookSecret}
                    title="Copy secret"
                  >
                    {copiedSecret ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={regenerateSecret}
                    title="Regenerate secret"
                  >
                    <RefreshCcw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-pbx-settings-btn">
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save PBX Settings
            </Button>
          </div>
        </TabsContent>

        {/* Lines Tab */}
        <TabsContent value="lines" className="space-y-6">
          <Card className="border-border/50" data-testid="all-lines-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="w-5 h-5 text-primary" />
                    All Lines
                  </CardTitle>
                  <CardDescription>
                    View and configure all PBX lines. Toggle Queue, Auto-Attendant, and After-Hours flags.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-0.5 bg-muted/50 rounded-lg p-1 border border-border/50">
                    {['all', 'queues', 'extensions', 'virtual'].map(f => (
                      <button
                        key={f}
                        onClick={() => setLinesFilter(f)}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                          linesFilter === f
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }`}
                        data-testid={`lines-filter-${f}`}
                      >
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchLines} data-testid="refresh-lines-btn">
                    <RefreshCw className={`w-4 h-4 ${loadingLines ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingLines ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : lines.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No lines found. Check PBX connection.</p>
              ) : (
                <div className="space-y-2">
                  {lines
                    .filter(line => {
                      if (linesFilter === 'all') return true;
                      if (linesFilter === 'queues') return line.IsQueue || line.Type === 'queue';
                      if (linesFilter === 'extensions') return line.Type === 'extension';
                      if (linesFilter === 'virtual') return ['virtual', 'voicemail', 'ddi'].includes(line.Type);
                      return true;
                    })
                    .map(line => {
                      const isQueue = line.IsQueue || line.Type === 'queue';
                      const hasAA = line.HasAutoAttendant;
                      const isAH = line.IsAfterHours;
                      const isMain = line.IsMainLine;
                      const isDDI = line.Type === 'ddi';
                      const formatPhone = (p) => {
                        if (!p) return '';
                        if (p.startsWith('64') && p.length > 9) return '0' + p.slice(2);
                        return p;
                      };
                      
                      return (
                        <div
                          key={line.ServiceName}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                            isMain ? 'border-l-4 border-l-emerald-500 border-border/50' : 
                            isQueue ? 'border-l-4 border-l-primary border-border/50' : 'border-border/30'
                          }`}
                          data-testid={`line-${line.ServiceName}`}
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="flex-shrink-0">
                              {isQueue ? (
                                <Users className="w-5 h-5 text-primary" />
                              ) : (
                                <Phone className="w-5 h-5 text-muted-foreground" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm truncate">{line.Name || line.DisplayName}</p>
                                {isDDI && <Badge variant="outline" className="text-xs border-amber-500 text-amber-500 flex-shrink-0">DDI</Badge>}
                                {isMain && <Badge variant="outline" className="text-xs border-emerald-500 text-emerald-500 flex-shrink-0">Main</Badge>}
                                {isQueue && <Badge variant="outline" className="text-xs border-primary text-primary flex-shrink-0">Queue</Badge>}
                                {hasAA && <Badge variant="outline" className="text-xs border-cyan-500 text-cyan-500 flex-shrink-0">AA</Badge>}
                                {isAH && <Badge variant="outline" className="text-xs border-amber-400 text-amber-400 flex-shrink-0">AH</Badge>}
                              </div>
                              <p className="text-xs text-muted-foreground font-mono">
                                {line.Extension ? `Ext ${line.Extension} \u2022 ` : ''}{formatPhone(line.ServiceName)}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4 flex-shrink-0">
                            <button
                              onClick={() => toggleLineQueue(line.ServiceName, !isQueue)}
                              className={`flex items-center gap-1 text-xs transition-colors ${
                                isQueue ? 'text-primary' : 'text-muted-foreground hover:text-primary'
                              }`}
                              title={isQueue ? 'Remove from queues' : 'Mark as queue'}
                              data-testid={`line-toggle-queue-${line.ServiceName}`}
                            >
                              {isQueue ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                              <span>Queue</span>
                            </button>
                            <button
                              onClick={() => toggleLineAA(line.ServiceName, !hasAA)}
                              className={`flex items-center gap-1 text-xs transition-colors ${
                                hasAA ? 'text-cyan-400' : 'text-muted-foreground hover:text-cyan-400'
                              }`}
                              title={hasAA ? 'Remove auto-attendant' : 'Mark as auto-attendant'}
                              data-testid={`line-toggle-aa-${line.ServiceName}`}
                            >
                              {hasAA ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                              <span>AA</span>
                            </button>
                            <button
                              onClick={() => toggleLineAH(line.ServiceName, !isAH)}
                              className={`flex items-center gap-1 text-xs transition-colors ${
                                isAH ? 'text-amber-400' : 'text-muted-foreground hover:text-amber-400'
                              }`}
                              title={isAH ? 'Remove after-hours' : 'Mark as after-hours'}
                              data-testid={`line-toggle-ah-${line.ServiceName}`}
                            >
                              {isAH ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                              <span>AH</span>
                            </button>
                            <button
                              onClick={() => toggleLineMain(line.ServiceName, !isMain)}
                              className={`flex items-center gap-1 text-xs transition-colors ${
                                isMain ? 'text-emerald-400' : 'text-muted-foreground hover:text-emerald-400'
                              }`}
                              title={isMain ? 'Remove main line (appears in both Queues & After Hours)' : 'Mark as main line (appears in both Queues & After Hours)'}
                              data-testid={`line-toggle-main-${line.ServiceName}`}
                            >
                              {isMain ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                              <span>Main</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Star Codes Tab */}
        <TabsContent value="starcodes" className="space-y-6">
          <StarCodesManager />
        </TabsContent>

        {/* Team Members Tab */}
        <TabsContent value="users" className="space-y-6">
          <Card className="border-border/50" data-testid="team-members-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" />
                    Team Members
                  </CardTitle>
                  <CardDescription>
                    Manage users in your company. New users can self-register using your account number.
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchUsers} disabled={loadingUsers}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingUsers ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingUsers ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No team members found</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Share your account number <span className="font-mono text-primary">{company?.account_number}</span> with team members so they can register.
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="relative group">
                              {u.profile_image ? (
                                <img 
                                  src={u.profile_image} 
                                  alt={u.name || 'User'} 
                                  className="w-10 h-10 rounded-full object-cover border-2 border-border"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center border-2 border-border">
                                  <User className="w-5 h-5 text-primary" />
                                </div>
                              )}
                              {/* Edit image button overlay */}
                              {u.id !== user?.id && (
                                <button
                                  onClick={() => openImageDialog(u)}
                                  className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                  data-testid={`edit-image-${u.id}`}
                                >
                                  <Camera className="w-4 h-4 text-white" />
                                </button>
                              )}
                            </div>
                            <div>
                              <p className="font-medium">{u.name || 'Unnamed'}</p>
                              {u.email && (
                                <p className="text-xs text-muted-foreground">{u.email}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm">{u.phone_number}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={u.is_active !== false ? 'default' : 'destructive'} className="text-xs">
                            {u.is_active !== false ? 'Active' : 'Disabled'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {u.id === user?.id ? (
                            getRoleBadge(u.role)
                          ) : (
                            <Select
                              value={u.role}
                              onValueChange={(value) => handleRoleChange(u.id, value)}
                            >
                              <SelectTrigger className="w-32 h-8" data-testid={`role-select-${u.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {getAvailableRoles().map(role => (
                                  <SelectItem key={role.value} value={role.value}>
                                    {role.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {u.id === user?.id ? (
                            <Badge variant="outline" className="text-xs">You</Badge>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`user-actions-${u.id}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {u.is_active !== false ? (
                                  <DropdownMenuItem
                                    onClick={() => handleToggleUserStatus(u.id, false)}
                                    data-testid={`disable-user-${u.id}`}
                                  >
                                    <UserX className="w-4 h-4 mr-2" />
                                    Disable User
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem
                                    onClick={() => handleToggleUserStatus(u.id, true)}
                                    data-testid={`enable-user-${u.id}`}
                                  >
                                    <UserCheck className="w-4 h-4 mr-2" />
                                    Enable User
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <DropdownMenuItem
                                      className="text-destructive focus:text-destructive"
                                      onSelect={(e) => e.preventDefault()}
                                      data-testid={`delete-user-${u.id}`}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete Permanently
                                    </DropdownMenuItem>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle className="flex items-center gap-2">
                                        <AlertTriangle className="w-5 h-5 text-destructive" />
                                        Permanently Delete User
                                      </AlertDialogTitle>
                                      <AlertDialogDescription asChild>
                                        <div className="space-y-3">
                                          <p>
                                            Are you sure you want to permanently delete <strong>{u.name || u.phone_number}</strong>?
                                          </p>
                                          <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                                            This will permanently remove this user's account and cannot be undone. Their call history and records will be preserved but they will no longer be able to log in.
                                          </div>
                                        </div>
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleRemoveUser(u.id)}
                                        className="bg-destructive hover:bg-destructive/90"
                                      >
                                        Delete Permanently
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Role Descriptions */}
          <Card className="border-border/50 border-dashed">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Shield className="w-4 h-4 text-muted-foreground" />
                Role Descriptions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div className="p-3 rounded-lg bg-secondary/30">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="default">Admin</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Full company access including settings and user management
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/30">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="secondary">Phone Admin</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Manage phone settings, view analytics and line status
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/30">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline">User</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Basic access: softphone, contacts, call history
                  </p>
                </div>
                {isDeveloper() && (
                  <div className="p-3 rounded-lg bg-secondary/30">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="destructive">Developer</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Full system access including developer tools
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Image Upload Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              Edit Profile Image
            </DialogTitle>
            <DialogDescription>
              {selectedUser && (
                <span>Update profile image for <strong>{selectedUser.name || selectedUser.phone_number}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Current Image Preview */}
            <div className="flex justify-center">
              {selectedUser?.profile_image ? (
                <img 
                  src={selectedUser.profile_image} 
                  alt={selectedUser?.name || 'User'} 
                  className="w-24 h-24 rounded-full object-cover border-4 border-border"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center border-4 border-border">
                  <User className="w-10 h-10 text-primary" />
                </div>
              )}
            </div>

            {/* Hidden file input */}
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              data-testid="image-file-input"
            />

            {/* Action Buttons */}
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => imageInputRef.current?.click()}
                disabled={uploadingImage}
                className="w-full"
                data-testid="upload-image-btn"
              >
                {uploadingImage ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {selectedUser?.profile_image ? 'Change Image' : 'Upload Image'}
              </Button>

              {selectedUser?.profile_image && (
                <Button
                  variant="outline"
                  onClick={handleRemoveImage}
                  disabled={uploadingImage}
                  className="w-full text-destructive hover:text-destructive"
                  data-testid="remove-image-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove Image
                </Button>
              )}
            </div>

            <p className="text-xs text-muted-foreground text-center">
              Accepted formats: JPG, PNG, GIF, WebP (max 5MB)
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
